import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Sparkles, Download } from 'lucide-react';

interface HeroSectionProps {
    onGetStarted: () => void;
}

export const HeroSection: React.FC<HeroSectionProps> = ({ onGetStarted }) => {
    const { scrollY } = useScroll();

    // Parallax & Reveal effect for the 3D image
    const rotate = useTransform(scrollY, [0, 600], [45, 0]);
    const scale = useTransform(scrollY, [0, 600], [0.85, 1.1]);
    const y = useTransform(scrollY, [0, 600], [100, 0]);
    const opacity = useTransform(scrollY, [0, 400], [0.5, 1]);

    return (
        <section className="hero-section relative min-h-screen flex flex-col items-center pt-24 pb-40 overflow-hidden">
            {/* Background Ambience */}
            <div className="hero-spotlight bg-blue-500/10" />

            {/* Content */}
            <div className="relative z-10 text-center px-4 max-w-5xl mx-auto mb-10">

                {/* Badge */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-blue-500/20 bg-blue-500/10 backdrop-blur-md text-sm text-blue-200 mb-8"
                >
                    <Sparkles className="w-3 h-3 text-blue-400" />
                    <span className="font-medium">Redefining Digital Thinking</span>
                </motion.div>

                {/* Headline */}
                <motion.h1
                    className="hero-title"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.7, delay: 0.1 }}
                >
                    Infinite Canvas for <br />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500">Visual Thinkers & AI Research.</span>
                </motion.h1>

                {/* Subtitle */}
                <motion.p
                    className="hero-subtitle"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.7, delay: 0.2 }}
                >
                    NoteSpace combines a limitless whiteboard with context-aware AI.
                    Real-time collaboration, structured knowledge graphs, study guides, and more in one fluid workspace.
                </motion.p>

                {/* CTA Buttons */}
                <motion.div
                    className="flex flex-wrap justify-center gap-4 mb-20"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                >
                    <button onClick={onGetStarted} className="btn-shiny flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 border-white/10 shadow-[0_0_20px_rgba(99,102,241,0.3)] hover:shadow-[0_0_30px_rgba(99,102,241,0.5)]">
                        Start Creating <ArrowRight className="w-4 h-4" />
                    </button>

                    <a href="#how-it-works" className="px-8 py-4 rounded-full font-semibold text-slate-300 hover:text-white transition flex items-center gap-2 bg-white/5 border border-white/5 hover:bg-white/10">
                        How it Works
                    </a>

                    <a href="/NoteSpace.msi" download className="px-8 py-4 rounded-full font-semibold text-slate-300 hover:text-white transition flex items-center gap-2 bg-white/5 border border-white/5 hover:bg-white/10 hover:border-blue-500/30">
                        <Download className="w-4 h-4" />
                        Download for Windows
                    </a>
                </motion.div>
            </div>

            {/* 3D App Preview - Scroll Driven */}
            <motion.div
                className="perspective-container w-full max-w-[100vw] px-4 md:px-0"
                style={{
                    perspective: 2000,
                    y
                }}
            >
                <motion.div
                    className="hero-image-wrapper mx-auto"
                    style={{
                        rotateX: rotate,
                        scale,
                        opacity
                    }}
                >
                    <img
                        src="/hero-preview.png"
                        alt="NoteSpace intuitive infinite canvas interface showing AI-generated flashcards, structural notes, and collaborative cursors"
                        className="w-full h-auto rounded-xl shadow-2xl border-4 border-slate-900/50"
                    />
                </motion.div>
            </motion.div>

        </section>
    );
};
